import {ArrowUpRightIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useEffect, useRef} from 'react'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'
import {ViewMode} from '../../../../comments/utils/view-mode'
import {routeToCommentsSection, useCurrentPathParams} from '../../../data/use-comments-routing'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'
import {MultiColorProgressBar} from './MultiColorProgressBar'
import {projectCommentsProgress} from '../comments-progress-projection'
import type {CommentsProgressProjection} from '../comments-progress-projection'
import type {MiniStatusAction} from './MiniMergeStatusBoundary'
import type {To} from '@github-ui/react-router'
import {useThreadCountsQuery} from '../../../../../page-data/loaders/use-tab-counts'
import type {QueryStatus} from '@github-ui/react-query'

export function CommentsMiniStatus() {
  const pathParams = useCurrentPathParams()
  const threadCountsQuery = useThreadCountsQuery()
  const projection = projectCommentsProgress(threadCountsQuery.data)
  const threadsStatus = threadCountsQuery.status

  return (
    <CommentsMiniStatusView
      to={routeToCommentsSection(pathParams, ViewMode.ThreadPreviews)}
      projection={projection}
      threadsStatus={threadsStatus}
    />
  )
}

type ThreadsStatus = QueryStatus

export function CommentsMiniStatusView({
  action,
  projection,
  threadsStatus = 'success',
  to,
}: {
  action?: MiniStatusAction
  projection: CommentsProgressProjection
  threadsStatus?: ThreadsStatus
  to?: To
}) {
  const isRefreshEnabled = isForYouRefreshEnabled()
  const isComplete = threadsStatus === 'success'
  const hasFailed = isRefreshEnabled && threadsStatus === 'error'
  const shouldAnnounce = isRefreshEnabled && isComplete
  const previousUnavailableRef = useRef(projection.isUnavailable)

  // FIXME: We should not be announcing on load/error for this query; we can just wait until users navigate to it.
  // This is creating distracting announcement noise on page load (also appies to announcements inside of MultiColorProgressBar).
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- External screen-reader announcements must wait for complete thread data.
    if (!shouldAnnounce) return
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- The interactive row remounts its progress subtree when comment data becomes unavailable.
    if (!previousUnavailableRef.current && projection.isUnavailable) announce('No comments available')
    if (previousUnavailableRef.current && !projection.isUnavailable) {
      announce(`${projection.blockingSummary ?? 'All comments resolved'}. ${projection.progressValueText}`)
    }
    previousUnavailableRef.current = projection.isUnavailable
  }, [shouldAnnounce, projection.blockingSummary, projection.isUnavailable, projection.progressValueText])

  const interactive = !projection.isUnavailable && (action !== undefined || to !== undefined)
  const summary = hasFailed
    ? 'Comments unavailable'
    : isComplete && !projection.isUnavailable
      ? projection.blockingSummary?.replace(/ comments?\b/g, '')
      : undefined
  return (
    <MiniMergeStatusItem
      aria-label={interactive ? 'View unresolved comments' : 'Resolved comments'}
      onClick={projection.isUnavailable || to || !action ? undefined : event => action(event.currentTarget)}
      to={projection.isUnavailable ? undefined : to}
    >
      <MiniMergeStatusItem.LabelRow
        icon={interactive ? ArrowUpRightIcon : undefined}
        summary={isRefreshEnabled ? summary : undefined}
      >
        <MiniMergeStatusItem.Label
          icon={projection.icon}
          isUnavailable={projection.isUnavailable}
          successful={hasFailed ? undefined : projection.successful}
          total={hasFailed ? undefined : projection.total}
        >
          {isRefreshEnabled ? 'Comments' : 'Resolved comments'}
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      {isRefreshEnabled ? (
        <MultiColorProgressBar
          aria-label={isComplete ? projection.progressLabel : 'Resolved comments'}
          aria-valuetext={projection.progressValueText}
          announceAvailabilityChanges={false}
          blockingSummary={isComplete ? projection.blockingSummary : undefined}
          clearedBlockingSummary="All comments resolved"
          max={projection.total}
          miniStatusSpacing
          semantics="breakdown"
          state={
            threadsStatus === 'error'
              ? {kind: 'unavailable', label: 'Resolved comments unavailable'}
              : threadsStatus === 'pending'
                ? {kind: 'unavailable', label: 'Loading comments'}
                : projection.isUnavailable
                  ? {kind: 'unavailable', label: 'No comments available'}
                  : {kind: 'available', segments: projection.segments}
          }
          value={projection.successful}
        />
      ) : (
        <MiniMergeStatusItem.Progress
          aria-label={projection.progressLabel}
          isUnavailable={projection.isUnavailable}
          successful={projection.successful}
          total={projection.total}
          unavailableLabel="No comments available"
        />
      )}
    </MiniMergeStatusItem>
  )
}

export function CommentsMiniStatusBoundary({action, children}: {action?: MiniStatusAction; children: React.ReactNode}) {
  const pathParams = useCurrentPathParams()
  return (
    <MiniMergeStatusBoundary
      action={action}
      actionLabel="View unresolved comments"
      boundaryName="CommentsMiniStatusBoundary"
      text={isForYouRefreshEnabled() ? 'Comments' : 'Resolved comments'}
      to={action ? undefined : routeToCommentsSection(pathParams, ViewMode.ThreadPreviews)}
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}
